import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import ZeditLogo from './ZeditLogo';

const productLinks = [
  { label: 'LayerSeek', href: '/layerseek', desc: 'DeFi virtual events' },
  { label: 'Clips', href: '/clips', desc: 'AI video clipper' },
  { label: 'TrueKOL', href: '/truekol', desc: 'Vetted KOL network' },
];

const ZeditHeader = ({ theme = 'light' }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const { pathname } = useLocation();
  const isDark = theme === 'dark';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <header
      data-testid="zedit-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? isDark
            ? 'bg-black/70 backdrop-blur-xl border-b border-white/10'
            : 'bg-white/85 backdrop-blur-xl border-b border-[var(--border-subtle)]'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Link to="/" data-testid="zedit-header-home">
            <ZeditLogo theme={theme} />
          </Link>

          <nav className="hidden lg:flex items-center gap-2">
            <div
              className="relative"
              onMouseEnter={() => setProductsOpen(true)}
              onMouseLeave={() => setProductsOpen(false)}
            >
              <button
                data-testid="nav-products"
                className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1.5 rounded-lg ${
                  isDark ? 'text-white/70 hover:text-white hover:bg-white/5' : 'text-[var(--text-secondary)] hover:text-[var(--zedit-ink)] hover:bg-[var(--bg-tertiary)]'
                }`}
              >
                Products
                <span className="text-[9px] opacity-60">▼</span>
              </button>
              <AnimatePresence>
                {productsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 4 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-0 pt-3 min-w-[280px]"
                    style={{ width: '280px', maxWidth: 'none' }}
                  >
                    <div
                      className={`rounded-2xl shadow-2xl p-2 ${
                        isDark
                          ? 'bg-[#0a0a0c] border border-white/10'
                          : 'bg-white border border-[var(--border-subtle)]'
                      }`}
                    >
                      {productLinks.map((p) => (
                        <Link
                          key={p.href}
                          to={p.href}
                          data-testid={`nav-product-${p.label.toLowerCase()}`}
                          className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl transition-colors group ${
                            isDark ? 'hover:bg-white/5' : 'hover:bg-[var(--bg-tertiary)]'
                          }`}
                        >
                          <div className="min-w-0">
                            <div className={`text-sm font-semibold whitespace-nowrap ${isDark ? 'text-white' : 'text-[var(--zedit-ink)]'}`}>
                              {p.label}
                            </div>
                            <div className={`text-xs mt-0.5 whitespace-nowrap ${isDark ? 'text-white/50' : 'text-[var(--text-muted)]'}`}>
                              {p.desc}
                            </div>
                          </div>
                          <ArrowUpRight
                            className={`w-4 h-4 flex-shrink-0 transition-colors ${
                              isDark
                                ? 'text-white/40 group-hover:text-[var(--accent-primary)]'
                                : 'text-[var(--text-muted)] group-hover:text-[var(--accent-hover)]'
                            }`}
                          />
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              to="/layerseek"
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-lg ${
                isDark ? 'text-white/70 hover:text-white hover:bg-white/5' : 'text-[var(--text-secondary)] hover:text-[var(--zedit-ink)] hover:bg-[var(--bg-tertiary)]'
              }`}
            >
              LayerSeek Event
            </Link>
            <a
              href="#contact"
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-lg ${
                isDark ? 'text-white/70 hover:text-white hover:bg-white/5' : 'text-[var(--text-secondary)] hover:text-[var(--zedit-ink)] hover:bg-[var(--bg-tertiary)]'
              }`}
            >
              Contact
            </a>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/layerseek"
              data-testid="header-cta-event"
              className={`text-sm font-medium px-4 py-2 rounded-xl border transition-colors ${
                isDark
                  ? 'border-white/20 text-white hover:border-white'
                  : 'border-[var(--border-primary)] hover:border-[var(--zedit-ink)] text-[var(--zedit-ink)]'
              }`}
            >
              Join Event
            </Link>
            <a
              href="#contact"
              data-testid="header-cta-talk"
              className={`text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors flex items-center gap-1.5 ${
                isDark
                  ? 'bg-white text-black hover:bg-[var(--accent-primary)]'
                  : 'bg-[var(--zedit-ink)] text-white hover:bg-black'
              }`}
            >
              Let&apos;s talk
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>

          <button
            className={`lg:hidden p-2 ${isDark ? 'text-white' : 'text-[var(--zedit-ink)]'}`}
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-[var(--border-subtle)] overflow-hidden"
          >
            <nav className="container mx-auto px-5 py-4 space-y-1">
              <div className="text-xs uppercase tracking-widest text-[var(--text-muted)] px-3 pt-2 pb-1">Products</div>
              {productLinks.map((p) => (
                <Link
                  key={p.href}
                  to={p.href}
                  className="flex items-center justify-between p-3 rounded-xl hover:bg-[var(--bg-tertiary)]"
                  onClick={() => setOpen(false)}
                >
                  <div>
                    <div className="text-sm font-semibold">{p.label}</div>
                    <div className="text-xs text-[var(--text-muted)]">{p.desc}</div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-[var(--text-muted)]" />
                </Link>
              ))}
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                className="block mt-3 text-center text-sm font-semibold px-5 py-3 rounded-xl bg-[var(--zedit-ink)] text-white"
              >
                Let&apos;s talk
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default ZeditHeader;
